import React, { useMemo, useState } from "react";
import "./SIPCalculator.css";

const formatCurrency = (num) => {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export default function SIPCalculator() {
  const [monthlyInvestment, setMonthlyInvestment] = useState(1000);
  const [years, setYears] = useState(5);
  const [rate, setRate] = useState(12);

  const data = useMemo(() => {
    const monthlyRate = rate / 100 / 12;
    const months = years * 12;

    // SIP Formula
    const maturity =
      monthlyInvestment *
      (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
        (1 + monthlyRate));

    const invested = monthlyInvestment * months;
    const returns = maturity - invested;
    const gain = (returns / invested) * 100;

    return {
      invested,
      maturity,
      returns,
      gain,
    };
  }, [monthlyInvestment, years, rate]);

  return (
    <div className="sip-page">
      <div className="sip-card">
        <div className="sip-title">
          <h1>SIP Calculator</h1>
          <p>Systematic Investment Plan</p>
        </div>

        <div className="sip-body">
          {/* LEFT */}
          <div className="sip-left">
            <div className="slider-box">
              <div className="slider-header">
                <span>Monthly Investment</span>
                <span>₹{formatCurrency(monthlyInvestment)}</span>
              </div>

              <input
                type="range"
                min="500"
                max="100000"
                step="500"
                value={monthlyInvestment}
                onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
              />
            </div>

            <div className="slider-box">
              <div className="slider-header">
                <span>Investment Period</span>
                <span>{years} Years</span>
              </div>

              <input
                type="range"
                min="2"
                max="13"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
              />
            </div>

            <div className="slider-box">
              <div className="slider-header">
                <span>Rate of Return</span>
                <span>{rate}% p.a.</span>
              </div>

              <input
                type="range"
                min="1"
                max="30"
                step="0.5"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
              />
            </div>
          </div>

          {/* RIGHT */}
          <div className="sip-right">
            <h2>Investment Summary</h2>

            <div className="summary-card">
              <p>Total Investment</p>
              <h3>₹{formatCurrency(data.invested)}</h3>
            </div>

            <div className="summary-card">
              <p>Expected Amount</p>
              <h3>₹{formatCurrency(data.maturity)}</h3>
            </div>

            <div className="summary-card">
              <p>Total Returns</p>
              <h3>₹{formatCurrency(data.returns)}</h3>
            </div>

            <div className="summary-card">
              <p>Wealth Gain</p>
              <h3>{data.gain.toFixed(1)}%</h3>
            </div>

            <div className="bottom-values">
              <div style={{ color: "white" }}>
                <span style={{ color: "white" }}>Invested</span>
                <h4>₹{formatCurrency(data.invested)}</h4>
              </div>

              <div style={{ color: "white" }}>
                <span style={{ color: "white" }}>Returns</span>
                <h4>₹{formatCurrency(data.returns)}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimers, Assumptions, AMFI Note, Educational Content, CTA */}
      {/* ========== STANDARD DISCLAIMER (BOLD & SEPARATE) ========== */}
      <div className="standard-disclaimer">
        <strong>
          Mutual Fund investments are subject to market risks, read all scheme
          related documents carefully.
        </strong>
      </div>

      {/* Disclaimers */}
      <div className="disclaimers-section">
        <div className="disclaimer-title">
          <span className="disclaimer-icon">ℹ️</span>
          Important Information &amp; Risk Warnings
        </div>

        {/* Updated past performance wording */}
        <div className="disclaimer-text">
          Past performance may or may not be sustained in future and is not a
          guarantee of any future returns. The calculations shown are for
          illustration purposes only and do not represent guaranteed or assured
          returns. Actual returns will vary based on market conditions, fund
          performance, and timing of investments.
        </div>

        <div className="disclaimer-text">
          <strong>Market Volatility:</strong> Actual returns will vary
          significantly based on market conditions, fund performance, and timing
          of investments. Equity investments are subject to higher volatility
          and risk.
        </div>

        <div className="disclaimer-text">
          <strong>No Guaranteed Returns:</strong> The calculations displayed are
          estimates based on assumed rates of return. These projections are not
          promises, guarantees, or assurances of actual future returns.
        </div>

        <div className="disclaimer-text">
          <strong>Consult Before Investing:</strong> Please read the Scheme
          Information Document (SID), Key Information Memorandum (KIM), and
          Statement of Additional Information (SAI) carefully before investing.
          Consider your risk appetite, investment objectives, and financial
          situation.
        </div>
      </div>

      {/* Client-provided detailed disclaimer */}
      <div className="educational-section">
        <p>
          The above results are based on an assumed rate of return. Please get
          in touch with your professional advisor for a detailed suggestion. The
          results are based on an assumed rate of return. The calculations are
          not based on any judgments of the future return of the debt and equity
          markets / sectors or of any individual security and should not be
          construed as promise on minimum returns and/or safeguard of capital.
          While utmost care has been exercised while preparing the calculator,
          JM Financial Mutual Fund / JM Financial Asset Management Limited does
          not warrant the completeness or guarantee that the achieved
          computations are flawless and/or accurate and disclaims all
          liabilities, losses and damages arising out of the use or in respect
          of anything done in reliance of the calculator. The examples do not
          purport to represent the performance of any scheme or security or
          investments. In view of individual nature of tax consequences, each
          investor is advised to consult his / her own professional tax /
          financial advisor before taking any investment decision.
        </p>
      </div>

      {/* Assumptions Box */}
      <div className="assumptions-box">
        <div className="assumptions-title">
          Assumptions Used in Calculation:
        </div>
        <ul className="assumptions-list">
          <li>
            Expected rate of return is an assumed value for illustration
            purposes only
          </li>
          <li>
            Calculations assume monthly investments with monthly compounding
          </li>
          <li>
            Returns shown are pre-tax and do not account for exit loads or
            expense ratios
          </li>
          <li>
            The rate of return is assumed constant for projection (actual
            returns will fluctuate)
          </li>
          <li>
            No consideration of inflation or taxes in the projected values
          </li>
          <li>
            Actual fund performance will vary and may be significantly higher or
            lower
          </li>
        </ul>
      </div>

      {/* AMFI Note */}
      <div className="amfi-note">
        <strong>Note:</strong> Returns calculated by taking mean of 10-year
        rolling returns between 01/06/14 and 31/05/24 (Index values are
        considered from June 2004 to May 2024) for various benchmarks. Mean
        returns are as follows: INR Gold 8.84%; Nifty 500: 12.80% and CRISIL
        Composite Bond Index: 7.85%.
      </div>

      {/* Educational Content - What is SIP Calculator */}
      <div className="educational-section">
        <div className="educational-title">What is a SIP Calculator?</div>
        <div className="educational-content">
          <p>
            A SIP calculator is a financial calculator which calculates how much
            corpus you can accumulate through a SIP over a specified investment
            tenure assuming a certain rate of return. There are two variants of
            SIP calculators (a) SIP Returns Calculator (b) Goal Based SIP
            Calculator.
          </p>

          <p>
            <strong>SIP Returns Calculator:</strong> In this SIP calculator, you
            will enter your monthly SIP investment, your investment tenure (how
            many years you will invest) and the expected rate of return. The SIP
            calculator will estimate how much corpus you will accumulate by the
            end of the investment tenure.
          </p>

          <h3>How Does a SIP Calculator Work?</h3>

          <p>
            Here we will describe how our SIP returns calculator works. In order
            to understand how SIP calculators work, you should familiarize
            yourself with the concept of <strong>compounding</strong>. You may
            be familiar with two types of interest -- simple interest and
            compound interest. In simple interest, you get interest only on the
            principal. In compound interest, you get interest on not just the
            principal but also on accumulated interest.
          </p>

          <p>
            For example, if your principal is Rs 100 and compound interest rate
            is 6%, then in the first year, you will get Rs 6 as interest. Next
            year, interest will be calculated on Rs 100 + 6 = Rs 106; so you
            will get Rs 6.36 as interest. In the following year, interest will
            be calculated on Rs 106 + 6.36 = Rs 112.36; so you will get Rs 6.74
            as interest. You can see that every year the interest you are
            getting is increasing; this is because you are earning interest on
            interest. This is known as compounding.
          </p>

          <p>
            In mutual funds, you get returns or profits on your investments.
            When the profits are re-invested you get profit on profits. The
            formula of compounding effect for one-time investments is:
          </p>

          <div className="formula-box">FV = P × (1 + r%)^n</div>

          <p>
            <strong>Where:</strong>
          </p>
          <ul>
            <li>
              <strong>FV</strong> = Future Value of Investment
            </li>
            <li>
              <strong>P</strong> = Investment Amount
            </li>
            <li>
              <strong>r</strong> = Compounded Annual Growth Rate (CAGR) or
              annualized return
            </li>
            <li>
              <strong>n</strong> = Investment tenure in years
            </li>
          </ul>

          <p>
            You can think of SIPs as a series of one-time investments. If you
            are doing monthly SIP of Rs 10,000 for five years, you can think of
            it as 60 one-time investments of Rs 10,000 each with reducing
            tenure. The first SIP instalment (Rs 10,000) remains invested for 60
            months, the second SIP instalment remains invested for 59 months,
            the third instalment for 58 months, the fourth instalment for 57
            months etc. The formula used by the SIP calculator is:
          </p>

          <div className="formula-box">
            FV = P × ({"{"}[1 + r]^n - 1{"}"} / r) × (1 + r)
          </div>

          <p>
            <strong>Where:</strong>
          </p>
          <ul>
            <li>
              <strong>FV</strong> = Future value
            </li>
            <li>
              <strong>P</strong> = SIP amount
            </li>
            <li>
              <strong>r</strong> = Expected rate of return per frequency period
            </li>
            <li>
              <strong>n</strong> = Number of instalments
            </li>
          </ul>

          <h3>Benefits of Using a SIP Calculator</h3>

          <ul>
            <li>
              SIP is a disciplined way of investing for your long term financial
              goals
            </li>
            <li>
              You do not need a large sum of money to invest in SIP. By
              investing from your regular savings through SIP you can start
              investing from a younger age and benefit from the power of
              compounding over long investment horizon
            </li>
            <li>
              Using our SIP calculator you can estimate how much corpus you can
              accumulate if you start investing a portion of your regular
              savings through SIPs
            </li>
            <li>
              You can use our SIP calculator to see if your current savings and
              investments are sufficient to achieve your financial goals
            </li>
            <li>
              You can consider managing your expenses better, so that you can
              invest more through SIP for your financial goals
            </li>
          </ul>
        </div>
      </div>

      {/* CTA Section */}
      {/* <div className="cta-section_sip">
        <a href="#start-sip" className="cta-button-sip cta-primary">
          Start Your SIP Journey
        </a>
        <a href="#explore-funds" className="cta-button-sip cta-secondary">
          Explore JM Financial Funds
        </a>
        <a href="#documents" className="cta-button-sip cta-secondary">
          View Scheme Documents
        </a>
      </div> */}
    </div>
  );
}
